import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Paper,
  Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { SupplierStatementService } from '../../services/supplierStatement.service';
import { SupplierService } from '../../services/supplier.service';
import { AuthService } from '../../services/auth.service';
import { loadSuppliersSafely } from '../../utils/supplierAccess';
import { Supplier } from '../../interfaces/procurement/supplier.interface';
import {
  ImportReport, OursRow, StatementReport, StatementStatus, StatementSummary, TheirsRow,
} from '../../interfaces/procurement/supplierStatement.interface';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import SearchableSelect from '../common/SearchableSelect';
import ScrollableTable from '../common/ScrollableTable';
import { formatMoney, humanizeEnum } from '../../utils/format';
import { formatDate, toLocalIsoDate, firstOfMonthLocalIso } from '../../utils/dateUtils';
import { saveBlob } from '../../utils/saveBlob';

const STATUS_OPTIONS: StatementStatus[] = ['DRAFT', 'RECONCILED'];
const oursKey = (o: OursRow) => `${o.type}:${o.id}`;
const oursLabel = (o: OursRow) => `${humanizeEnum(o.type)} ${o.docNumber} (${formatDate(o.date)}) ${formatMoney(Math.abs(o.signed))}`;

/**
 * B4-G. Supplier statement reconciliation: the statement list, the import dialog with a dry-run preview and the match
 * workspace (their lines against our payables: auto-match, manual match / unmatch, sign-off, XLSX, confirmation letter).
 * A RECONCILED statement is read-only.
 */
const signedMoney = (n: number) => (n > 0 ? `+${formatMoney(n)}` : formatMoney(n));

const SupplierStatementReconPage: React.FC = () => {
  const toast = useToast();
  const institutionId = AuthService.getSelectedInstitution() as string;
  const canManage = usePermission(PERMISSIONS.SUPPLIER_BILL_MANAGE);

  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [rows, setRows] = useState<StatementSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterSupplier, setFilterSupplier] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // import dialog
  const [importOpen, setImportOpen] = useState(false);
  const [supplierId, setSupplierId] = useState('');
  const [periodFrom, setPeriodFrom] = useState(firstOfMonthLocalIso());
  const [periodTo, setPeriodTo] = useState(toLocalIsoDate());
  const [opening, setOpening] = useState('');
  const [closing, setClosing] = useState('');
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ImportReport | null>(null);
  const [busy, setBusy] = useState(false);

  // workspace
  const [report, setReport] = useState<StatementReport | null>(null);
  const [matchLine, setMatchLine] = useState<TheirsRow | null>(null);
  const [matchChoice, setMatchChoice] = useState('');
  const [signOpen, setSignOpen] = useState(false);
  const [signNote, setSignNote] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<StatementSummary | null>(null);

  const readOnly = !!report && report.status !== 'DRAFT';
  const signOffBlock = !report ? null
    : report.unexplained !== 0 ? 'The statement has an unexplained difference.'
    : report.closingSource === 'DERIVED' ? 'The closing balance was derived from the lines; import again with the supplier\'s real closing balance.'
    : report.onlyTheirs.some(t => t.orphanedMatch) ? 'A matched document is no longer a live payable; unmatch those lines first.'
    : null;
  const editable = canManage && !readOnly;

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setRows(await SupplierStatementService.list(institutionId, filterSupplier || undefined, (filterStatus || undefined) as StatementStatus | undefined));
    } catch (e) {
      toast.showApiError(e, 'Failed to load supplier statements');
    } finally {
      setLoading(false);
    }
  }, [institutionId, filterSupplier, filterStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    loadSuppliersSafely(() => SupplierService.listByInstitution(0, 500, 'ALL')).then(r => setSuppliers(r.suppliers));
  }, []);

  const params = () => ({
    supplierId, periodFrom, periodTo, notes,
    openingBalance: opening === '' ? null : Number(opening),
    closingBalance: closing === '' ? null : Number(closing),
  });

  const openImport = () => {
    setSupplierId(''); setOpening(''); setClosing(''); setNotes(''); setFile(null); setPreview(null);
    setPeriodFrom(firstOfMonthLocalIso()); setPeriodTo(toLocalIsoDate());
    setImportOpen(true);
  };

  const runPreview = async () => {
    if (!file) return;
    try {
      setBusy(true);
      setPreview(await SupplierStatementService.dryRun(institutionId, file, params()));
    } catch (e) {
      setPreview(null);
      toast.showApiError(e, 'Failed to read the statement file');
    } finally {
      setBusy(false);
    }
  };

  const runImport = async () => {
    if (!file) return;
    let created: StatementSummary;
    try {
      setBusy(true);
      created = await SupplierStatementService.import(institutionId, file, params());
    } catch (e) {
      toast.showApiError(e, 'Failed to import the statement');
      setBusy(false);
      return;
    }
    // the statement now exists: whatever happens next, close the dialog and open it
    setImportOpen(false);
    load();
    let autoText = '';
    try {
      const auto = await SupplierStatementService.autoMatch(institutionId, created.id);
      autoText = `Auto-matched ${auto.totalMatched} of ${created.lineCount} lines.`;
      toast.showSuccess(`Statement imported. ${autoText}`);
    } catch (e) {
      toast.showWarning('The statement was imported but auto-match failed. Use Auto-match in the workspace to retry.');
    }
    try {
      setSignNote('');
      setReport(await SupplierStatementService.report(institutionId, created.id));
    } catch (e) {
      toast.showApiError(e, 'The statement was imported but could not be opened');
    } finally {
      setBusy(false);
    }
  };

  const openStatement = async (id: string) => {
    try {
      setSignNote('');
      setReport(await SupplierStatementService.report(institutionId, id));
    } catch (e) {
      toast.showApiError(e, 'Failed to open the statement');
    }
  };

  const act = async (fn: () => Promise<StatementReport | void>, ok?: string) => {
    try {
      setBusy(true);
      const r = await fn();
      if (r) setReport(r);
      if (ok) toast.showSuccess(ok);
    } catch (e) {
      toast.showApiError(e, 'The action failed');
      if (report) openStatement(report.id);   // another request may have changed it: show the current state
    } finally {
      setBusy(false);
    }
  };

  const autoMatch = () => report && act(async () => {
    const res = await SupplierStatementService.autoMatch(institutionId, report.id);
    toast.showSuccess(`Matched ${res.matchedByReference} by reference and ${res.matchedByAmount} by amount`);
    return SupplierStatementService.report(institutionId, report.id);
  });

  const doMatch = () => {
    if (!report || !matchLine || !matchChoice) return;
    const [docType, docId] = matchChoice.split(':');
    act(() => SupplierStatementService.match(institutionId, report.id, matchLine.lineId, docType as never, docId), 'Matched');
    setMatchLine(null); setMatchChoice('');
  };

  const download = async (kind: 'xlsx' | 'letter') => {
    if (!report) return;
    try {
      const blob = kind === 'xlsx'
        ? await SupplierStatementService.downloadXlsx(institutionId, report.id)
        : await SupplierStatementService.downloadLetter(institutionId, report.id);
      saveBlob(blob, kind === 'xlsx' ? `supplier-reconciliation-${report.periodTo}.xlsx` : `balance-confirmation-${report.periodTo}.pdf`);
    } catch (e) {
      toast.showApiError(e, 'Failed to download');
    }
  };

  const removeStatement = async (s: StatementSummary) => {
    try {
      await SupplierStatementService.remove(institutionId, s.id);
      toast.showSuccess('Statement deleted');
      load();
    } catch (e) {
      toast.showApiError(e, 'Failed to delete the statement');
    }
  };

  const candidates = (line: TheirsRow | null): OursRow[] =>
    !report || !line ? [] : report.onlyOurs.filter(o => (line.signed > 0) === (o.signed > 0));

  // ------------------------------------------------------------------ workspace
  if (report) {
    const summaryCell = (label: string, value: number, emphasis = false) => (
      <Paper variant="outlined" sx={{ p: 1.5, flex: '1 1 180px', maxWidth: 260 }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="h6" color={emphasis && value !== 0 ? 'error' : undefined}>{formatMoney(value)}</Typography>
      </Paper>
    );
    return (
      <Box sx={{ p: 2 }} data-help-page="procurement.stmtRecon.rep.page">
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }}>
          <Button data-help="procurement.stmtRecon.rep.back" onClick={() => { setReport(null); load(); }}>Back to statements</Button>
          <Typography variant="h5" sx={{ flex: '1 1 260px' }}>
            {report.supplierName}: {formatDate(report.periodFrom)} to {formatDate(report.periodTo)}
          </Typography>
          <Chip label={humanizeEnum(report.status)} color={readOnly ? 'success' : 'default'} />
          <Button data-help="procurement.stmtRecon.rep.excel" variant="outlined" onClick={() => download('xlsx')}>Excel</Button>
          <Button data-help="procurement.stmtRecon.rep.letter" variant="outlined" onClick={() => download('letter')}>Confirmation letter (PDF)</Button>
          {editable && <Button data-help="procurement.stmtRecon.rep.auto" variant="outlined" disabled={busy} onClick={autoMatch}>Auto-match</Button>}
          {editable && <Button data-help="procurement.stmtRecon.rep.sign" variant="contained" disabled={busy || !!signOffBlock} onClick={() => setSignOpen(true)}>Sign off</Button>}
        </Box>
        {editable && signOffBlock && <Alert severity="warning" sx={{ mb: 2 }}>Cannot sign off yet: {signOffBlock}</Alert>}
        {readOnly && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Reconciled{report.signedOffDate ? ` on ${formatDate(report.signedOffDate)}` : ''}. This statement is read-only.
            {report.signOffNote ? ` Note: ${report.signOffNote}` : ''}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }} data-help-section="procurement.stmtRecon.rep.summary">
          {summaryCell('Their closing balance', report.theirsClosing)}
          {summaryCell('Our closing balance', report.oursClosing)}
          {summaryCell('Difference (theirs less ours)', report.difference, true)}
          {summaryCell('Unexplained', report.unexplained, true)}
        </Box>
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }} data-help-section="procurement.stmtRecon.rep.explain">
          <Typography variant="subtitle2" gutterBottom>How the difference is explained</Typography>
          <Table size="small">
            <TableBody>
              <TableRow><TableCell>Opening balance difference</TableCell><TableCell align="right">{signedMoney(report.openingDifference)}</TableCell></TableRow>
              <TableRow><TableCell>Matched documents with a different amount</TableCell><TableCell align="right">{signedMoney(report.matchedAmountDifference)}</TableCell></TableRow>
              <TableRow><TableCell>On their statement only (+ bills we have not booked, - payments or credits missing from our books)</TableCell><TableCell align="right">{signedMoney(report.onlyTheirsTotal)}</TableCell></TableRow>
              <TableRow><TableCell>In our books only (+ payments in transit, - our bills they lack)</TableCell><TableCell align="right">{signedMoney(-report.onlyOursTotal)}</TableCell></TableRow>
              <TableRow><TableCell><b>Total explained</b></TableCell><TableCell align="right"><b>{formatMoney(report.difference - report.unexplained)}</b></TableCell></TableRow>
            </TableBody>
          </Table>
        </Paper>

        <Typography variant="h6" sx={{ mt: 2 }}>Matched ({report.matched.length})</Typography>
        <ScrollableTable>
          <Table size="small" data-help-section="procurement.stmtRecon.m.table">
            <TableHead><TableRow>
              <TableCell data-help="procurement.stmtRecon.m.line">Line</TableCell><TableCell data-help="procurement.stmtRecon.m.date">Their date</TableCell><TableCell data-help="procurement.stmtRecon.m.ref">Their ref</TableCell>
              <TableCell data-help="procurement.stmtRecon.m.theirs" align="right">Their amount</TableCell><TableCell data-help="procurement.stmtRecon.m.doc">Our document</TableCell>
              <TableCell data-help="procurement.stmtRecon.m.ours" align="right">Our amount</TableCell><TableCell data-help="procurement.stmtRecon.m.diff" align="right">Difference</TableCell>
              <TableCell data-help="procurement.stmtRecon.m.how">How</TableCell><TableCell data-help="procurement.stmtRecon.m.actions" />
            </TableRow></TableHead>
            <TableBody>
              {report.matched.length === 0 && <TableRow><TableCell colSpan={9}>No matches yet.</TableCell></TableRow>}
              {report.matched.map(m => (
                <TableRow key={m.lineId}>
                  <TableCell>{m.lineNo}</TableCell><TableCell>{formatDate(m.date)}</TableCell><TableCell>{m.reference}</TableCell>
                  <TableCell align="right">{formatMoney(m.theirsAmount)}</TableCell>
                  <TableCell>{humanizeEnum(m.ourType)} {m.ourNumber}</TableCell>
                  <TableCell align="right">{formatMoney(m.ourAmount)}</TableCell>
                  <TableCell align="right">{m.difference !== 0 ? <Chip size="small" color="warning" label={formatMoney(m.difference)} /> : formatMoney(0)}</TableCell>
                  <TableCell>{humanizeEnum(m.method)}</TableCell>
                  <TableCell>{editable && <Button data-help="procurement.stmtRecon.m.unmatch" size="small" disabled={busy} onClick={() => act(() => SupplierStatementService.unmatch(institutionId, report.id, m.lineId), 'Unmatched')}>Unmatch</Button>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ScrollableTable>

        <Typography variant="h6" sx={{ mt: 3 }}>On their statement only ({report.onlyTheirs.length})</Typography>
        <ScrollableTable>
          <Table size="small" data-help-section="procurement.stmtRecon.t.table">
            <TableHead><TableRow>
              <TableCell data-help="procurement.stmtRecon.t.line">Line</TableCell><TableCell data-help="procurement.stmtRecon.t.date">Date</TableCell><TableCell data-help="procurement.stmtRecon.t.ref">Reference</TableCell><TableCell data-help="procurement.stmtRecon.t.desc">Description</TableCell>
              <TableCell data-help="procurement.stmtRecon.t.debit" align="right">Debit</TableCell><TableCell data-help="procurement.stmtRecon.t.credit" align="right">Credit</TableCell><TableCell data-help="procurement.stmtRecon.t.why">Why</TableCell><TableCell data-help="procurement.stmtRecon.t.actions" />
            </TableRow></TableHead>
            <TableBody>
              {report.onlyTheirs.length === 0 && <TableRow><TableCell colSpan={8}>Every line is matched.</TableCell></TableRow>}
              {report.onlyTheirs.map(t => (
                <TableRow key={t.lineId}>
                  <TableCell>{t.lineNo}</TableCell><TableCell>{formatDate(t.date)}</TableCell><TableCell>{t.reference}</TableCell>
                  <TableCell>{t.description}</TableCell>
                  <TableCell align="right">{t.debit ? formatMoney(t.debit) : ''}</TableCell>
                  <TableCell align="right">{t.credit ? formatMoney(t.credit) : ''}</TableCell>
                  <TableCell>{t.explanation}</TableCell>
                  <TableCell>
                    {editable && t.orphanedMatch && <Button data-help="procurement.stmtRecon.t.unmatch" size="small" color="warning" disabled={busy} onClick={() => act(() => SupplierStatementService.unmatch(institutionId, report.id, t.lineId), 'Unmatched')}>Unmatch</Button>}
                    {editable && <Button data-help="procurement.stmtRecon.t.match" size="small" onClick={() => { setMatchLine(t); setMatchChoice(''); }}>Match to...</Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ScrollableTable>

        <Typography variant="h6" sx={{ mt: 3 }}>In our books only ({report.onlyOurs.length})</Typography>
        <ScrollableTable>
          <Table size="small" data-help-section="procurement.stmtRecon.o.table">
            <TableHead><TableRow>
              <TableCell data-help="procurement.stmtRecon.o.type">Type</TableCell><TableCell data-help="procurement.stmtRecon.o.number">Number</TableCell><TableCell data-help="procurement.stmtRecon.o.ref">Reference</TableCell><TableCell data-help="procurement.stmtRecon.o.date">Date</TableCell>
              <TableCell data-help="procurement.stmtRecon.o.amount" align="right">Amount</TableCell><TableCell data-help="procurement.stmtRecon.o.why">Why</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {report.onlyOurs.length === 0 && <TableRow><TableCell colSpan={6}>Nothing outstanding on our side.</TableCell></TableRow>}
              {report.onlyOurs.map(o => (
                <TableRow key={oursKey(o)}>
                  <TableCell>{humanizeEnum(o.type)}</TableCell><TableCell>{o.docNumber}</TableCell><TableCell>{o.reference}</TableCell>
                  <TableCell>{formatDate(o.date)}</TableCell><TableCell align="right">{formatMoney(Math.abs(o.signed))}</TableCell>
                  <TableCell>{o.explanation}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ScrollableTable>

        <Dialog data-help-page="procurement.stmtMatchForm.page" open={!!matchLine} onClose={() => setMatchLine(null)} maxWidth="sm" fullWidth>
          <DialogTitle>Match line {matchLine?.lineNo}</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {matchLine && `${matchLine.reference ?? ''} ${formatDate(matchLine.date)}: ${matchLine.debit ? 'debit' : 'credit'} ${formatMoney(Math.abs(matchLine.signed))}`}
            </Typography>
            <SearchableSelect<OursRow> data-help="procurement.stmtMatchForm.field.doc" label="Our document" options={candidates(matchLine)} value={matchChoice}
              getOptionId={oursKey} getOptionLabel={oursLabel} onChange={id => setMatchChoice(id)}
              noOptionsText="No unmatched document of the right kind" />
          </DialogContent>
          <DialogActions>
            <Button data-help="procurement.stmtMatchForm.cancel" onClick={() => setMatchLine(null)}>Cancel</Button>
            <Button data-help="procurement.stmtRecon.dlg.match" variant="contained" disabled={!matchChoice || busy} onClick={doMatch}>Match</Button>
          </DialogActions>
        </Dialog>

        <Dialog data-help-page="procurement.stmtSignForm.page" open={signOpen} onClose={() => setSignOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Sign off this reconciliation</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2 }}>
              The statement becomes read-only. The remaining difference of {formatMoney(report.difference)} is accepted as explained by the listed reconciling items.
            </Typography>
            <TextField data-help="procurement.stmtSignForm.field.note" label="Note (optional)" value={signNote} onChange={e => setSignNote(e.target.value)} multiline minRows={2}
              inputProps={{ maxLength: 1000 }} sx={{ width: '100%' }} />
          </DialogContent>
          <DialogActions>
            <Button data-help="procurement.stmtSignForm.cancel" onClick={() => setSignOpen(false)}>Cancel</Button>
            <Button data-help="procurement.stmtRecon.dlg.sign" variant="contained" disabled={busy} onClick={() => {
              setSignOpen(false);
              act(() => SupplierStatementService.signOff(institutionId, report.id, signNote), 'Statement reconciled');
              setSignNote('');
            }}>Sign off</Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // ------------------------------------------------------------------ list
  const importReady = !!preview && preview.errorCount === 0 && preview.batchErrors.length === 0 && !preview.duplicateOfStatementId && preview.rowCount > 0;
  return (
    <Box sx={{ p: 2 }} data-help-page="procurement.stmtRecon.list.page">
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" sx={{ flex: '1 1 260px' }}>Supplier statement reconciliation</Typography>
        {canManage && <Button data-help="procurement.stmtRecon.list.import" variant="contained" onClick={openImport}>Import statement</Button>}
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }} data-help-section="procurement.stmtRecon.list.filter">
        <Box sx={{ flex: '1 1 260px', maxWidth: 380 }}>
          <SearchableSelect<Supplier> data-help="procurement.stmtRecon.list.filterSupplier" label="Supplier" options={suppliers} value={filterSupplier} getOptionLabel={s => s.name}
            onChange={id => setFilterSupplier(id)} />
        </Box>
        <Box sx={{ flex: '1 1 160px', maxWidth: 220 }}>
          <TextField data-help="procurement.stmtRecon.list.filterStatus" select label="Status" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} sx={{ width: '100%' }}>
            <MenuItem value="">All</MenuItem>
            {STATUS_OPTIONS.map(s => <MenuItem key={s} value={s}>{humanizeEnum(s)}</MenuItem>)}
          </TextField>
        </Box>
      </Box>
      {loading ? <CircularProgress /> : (
        <ScrollableTable>
          <Table size="small" data-help-section="procurement.stmtRecon.list.table">
            <TableHead><TableRow>
              <TableCell data-help="procurement.stmtRecon.list.supplier">Supplier</TableCell><TableCell data-help="procurement.stmtRecon.list.period">Period</TableCell><TableCell data-help="procurement.stmtRecon.list.closing" align="right">Their closing</TableCell>
              <TableCell data-help="procurement.stmtRecon.list.matched" align="right">Lines matched</TableCell><TableCell data-help="procurement.stmtRecon.list.status">Status</TableCell><TableCell data-help="procurement.stmtRecon.list.actions" />
            </TableRow></TableHead>
            <TableBody>
              {rows.length === 0 && <TableRow><TableCell colSpan={6}>No supplier statements yet.</TableCell></TableRow>}
              {rows.map(s => (
                <TableRow key={s.id} hover sx={{ cursor: 'pointer' }} data-testid={`statement-row-${s.id}`} onClick={() => openStatement(s.id)}>
                  <TableCell>{s.supplierName}</TableCell>
                  <TableCell>{formatDate(s.periodFrom)} to {formatDate(s.periodTo)}</TableCell>
                  <TableCell align="right">{formatMoney(s.closingBalanceTheirs)}</TableCell>
                  <TableCell align="right">{s.matchedCount} / {s.lineCount}</TableCell>
                  <TableCell><Chip size="small" label={humanizeEnum(s.status)} color={s.status === 'RECONCILED' ? 'success' : 'default'} /></TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Button data-help="procurement.stmtRecon.list.open" size="small" onClick={e => { e.stopPropagation(); openStatement(s.id); }}>{s.status === 'DRAFT' && canManage ? 'Reconcile' : 'Open'}</Button>
                      {canManage && s.status === 'DRAFT' && <Button data-help="procurement.stmtRecon.list.delete" size="small" color="error" onClick={e => { e.stopPropagation(); setDeleteTarget(s); }}>Delete</Button>}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ScrollableTable>
      )}

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete this statement?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {deleteTarget && `The draft statement of ${deleteTarget.supplierName} for ${formatDate(deleteTarget.periodFrom)} to ${formatDate(deleteTarget.periodTo)} and its matches will be removed.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button data-help="procurement.stmtRecon.list.deleteConfirm" color="error" variant="contained" onClick={() => { const t = deleteTarget; setDeleteTarget(null); if (t) removeStatement(t); }}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Dialog data-help-page="procurement.stmtImportForm.page" open={importOpen} onClose={() => setImportOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Import a supplier statement</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            CSV or XLSX with the columns date, reference, description, debit, credit and an optional balance. Debit is
            what the supplier billed us; credit is a payment or credit they applied.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1, mb: 2 }}>
            <Box sx={{ flex: '1 1 260px', maxWidth: 400 }}>
              <SearchableSelect<Supplier> data-help="procurement.stmtImportForm.field.supplier" label="Supplier" options={suppliers} value={supplierId} required
                getOptionLabel={s => s.name} onChange={id => { setSupplierId(id); setPreview(null); }} />
            </Box>
            <Box sx={{ flex: '1 1 150px', maxWidth: 200 }}>
              <TextField data-help="procurement.stmtImportForm.field.from" label="Period from" type="date" value={periodFrom} InputLabelProps={{ shrink: true }}
                onChange={e => { setPeriodFrom(e.target.value); setPreview(null); }} sx={{ width: '100%' }} />
            </Box>
            <Box sx={{ flex: '1 1 150px', maxWidth: 200 }}>
              <TextField data-help="procurement.stmtImportForm.field.to" label="Period to" type="date" value={periodTo} InputLabelProps={{ shrink: true }}
                onChange={e => { setPeriodTo(e.target.value); setPreview(null); }} sx={{ width: '100%' }} />
            </Box>
            <Box sx={{ flex: '1 1 150px', maxWidth: 200 }}>
              <TextField data-help="procurement.stmtImportForm.field.opening" label="Opening balance (optional)" type="number" value={opening}
                onChange={e => { setOpening(e.target.value); setPreview(null); }} sx={{ width: '100%' }} />
            </Box>
            <Box sx={{ flex: '1 1 150px', maxWidth: 200 }}>
              <TextField data-help="procurement.stmtImportForm.field.closing" label="Closing balance (optional)" type="number" value={closing}
                onChange={e => { setClosing(e.target.value); setPreview(null); }} sx={{ width: '100%' }} />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
            <Button data-help="procurement.stmtRecon.dlg.file" variant="outlined" component="label">
              {file ? file.name : 'Choose file'}
              <input type="file" hidden accept=".csv,.xlsx" data-testid="statement-file" aria-label="Statement file"
                onChange={e => { setFile(e.target.files?.[0] ?? null); setPreview(null); }} />
            </Button>
            <Button data-help="procurement.stmtRecon.dlg.preview" variant="contained" disabled={!file || !supplierId || busy} onClick={runPreview}>Preview</Button>
          </Box>
          {preview && (
            <>
              {preview.duplicateOfStatementId && <Alert severity="warning" sx={{ mb: 1 }}>This exact file was already imported for this supplier.</Alert>}
              {preview.warnings.map(w => <Alert key={w} severity="warning" sx={{ mb: 1 }}>{w}</Alert>)}
              {preview.batchErrors.map(b => <Alert key={b} severity="error" sx={{ mb: 1 }}>{b}</Alert>)}
              <Alert severity={preview.errorCount === 0 ? 'success' : 'error'} sx={{ mb: 1 }}>
                {preview.rowCount} lines, {preview.errorCount} with errors. Debits {formatMoney(preview.totalDebit)}, credits{' '}
                {formatMoney(preview.totalCredit)}. Their balance {formatMoney(preview.openingBalance)} to {formatMoney(preview.closingBalance)}.
              </Alert>
              <ScrollableTable>
                <Table size="small" data-help-section="procurement.stmtRecon.imp.table">
                  <TableHead><TableRow>
                    <TableCell data-help="procurement.stmtRecon.imp.row">Row</TableCell><TableCell data-help="procurement.stmtRecon.imp.date">Date</TableCell><TableCell data-help="procurement.stmtRecon.imp.ref">Reference</TableCell>
                    <TableCell data-help="procurement.stmtRecon.imp.debit" align="right">Debit</TableCell><TableCell data-help="procurement.stmtRecon.imp.credit" align="right">Credit</TableCell><TableCell data-help="procurement.stmtRecon.imp.problems">Problems</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {preview.rows.map(r => (
                      <TableRow key={r.rowNo}>
                        <TableCell>{r.rowNo}</TableCell><TableCell>{r.date ? formatDate(r.date) : ''}</TableCell><TableCell>{r.reference}</TableCell>
                        <TableCell align="right">{r.debit ? formatMoney(r.debit) : ''}</TableCell>
                        <TableCell align="right">{r.credit ? formatMoney(r.credit) : ''}</TableCell>
                        <TableCell sx={{ color: 'error.main' }}>{r.errors.join('; ')}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollableTable>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button data-help="procurement.stmtImportForm.cancel" onClick={() => setImportOpen(false)}>Cancel</Button>
          <Button data-help="procurement.stmtRecon.dlg.import" variant="contained" disabled={!importReady || busy} onClick={runImport}>Import and auto-match</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SupplierStatementReconPage;
